import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { SalesInvoiceTemplateRenderer } from '../components/SalesInvoiceTemplateRenderer';

/**
 * Export a sales order (invoice) to PDF matching the company's exact print template configuration.
 *
 * @param {Object} order - Sales order with items, customer, totals, etc.
 * @param {Object} company - Company configuration settings.
 * @param {Object} [options]
 * @param {string} [options.filename] - Base name for download/sharing.
 * @param {string} [options.itemFormat] - Item name format override.
 * @param {string} [options.templateKey] - Print template override.
 * @param {string} [options.paperType] - Paper type override (a4, a5, letter, thermal).
 * @param {boolean} [options.showTax=true] - Whether to show tax breakdown.
 * @param {HTMLElement} [options.element] - Optional already-mounted DOM element to capture.
 * @param {boolean} [options.save=true] - If true, triggers browser download; if false, returns ArrayBuffer.
 * @returns {Promise<ArrayBuffer|undefined>}
 */
export async function exportSalesOrderToPdf(order, company, options = {}) {
  const {
    filename = (order?.invoiceNumber || order?.orderNumber || 'invoice').replace(/\s+/g, '-'),
    itemFormat = company?.printItemNameFormat || 'name_sku',
    templateKey = company?.salesPrintTemplate || 'standard',
    currency = company?.currency || 'USD',
    dateFormat = company?.dateFormat || 'dd/MM/yyyy',
    showTax = options.showTax !== undefined ? options.showTax : true,
    save = true,
    element: passedElement = null,
  } = options;

  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const paperType = options.paperType || company?.printPaperType || (isAndroid ? 'letter' : 'a4');
  const isThermal = templateKey === 'thermal' || paperType === 'thermal';
  const isA5 = templateKey === 'a5' || paperType === 'a5';
  const isLetter = paperType === 'letter';

  // Container width in px for offscreen rendering
  const containerWidthPx = isThermal ? 380 : (isA5 ? 560 : (isLetter ? 816 : 800));

  let canvas = null;

  try {
    let targetElement = passedElement;
    let cleanup = null;

    if (!targetElement || !targetElement.isConnected) {
      // Create off-screen rendering container
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-99999px';
      container.style.top = '0';
      container.style.width = `${containerWidthPx}px`;
      container.style.backgroundColor = '#ffffff';
      container.style.zIndex = '-9999';
      container.style.boxSizing = 'border-box';
      container.style.overflow = 'visible';
      container.className = 'pdf-export-container';
      document.body.appendChild(container);

      const root = createRoot(container);
      root.render(
        <div style={{ width: '100%', background: '#ffffff', boxSizing: 'border-box' }}>
          <SalesInvoiceTemplateRenderer
            order={order}
            company={company}
            templateKey={templateKey}
            currency={currency}
            itemFormat={itemFormat}
            dateFormat={dateFormat}
            showTax={showTax}
          />
        </div>
      );

      // Wait for React rendering and any images to load
      await new Promise((resolve) => setTimeout(resolve, 250));

      const imgs = Array.from(container.querySelectorAll('img'));
      await Promise.all(
        imgs.map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            setTimeout(resolve, 1500);
          });
        })
      );

      await new Promise((resolve) => setTimeout(resolve, 100));

      targetElement = container;
      cleanup = () => {
        try {
          root.unmount();
        } catch (e) {
          // ignore
        }
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      };
    }

    // Capture the target element to canvas with high resolution
    canvas = await html2canvas(targetElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: containerWidthPx,
    });

    if (cleanup) {
      cleanup();
    }
  } catch (err) {
    console.error('html2canvas rendering error during PDF export:', err);
  }

  if (!canvas) {
    throw new Error('Failed to render invoice template to canvas');
  }

  // Create jsPDF instance
  let doc;
  if (isThermal) {
    const thermalWidthMm = 80;
    const thermalHeightMm = Math.max(80, (canvas.height * thermalWidthMm) / canvas.width);
    doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [thermalWidthMm, Math.ceil(thermalHeightMm)],
      compress: true,
    });
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, thermalWidthMm, thermalHeightMm);
  } else {
    const pdfFormat = isA5 ? 'a5' : (isLetter ? 'letter' : 'a4');
    doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: pdfFormat,
      compress: true,
    });

    const pdfPageWidth = doc.internal.pageSize.getWidth();
    const pdfPageHeight = doc.internal.pageSize.getHeight();
    const totalHeightMm = (canvas.height * pdfPageWidth) / canvas.width;

    if (totalHeightMm <= pdfPageHeight + 2) {
      // Single page fits entirely
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfPageWidth, totalHeightMm);
    } else {
      // Multi-page slicing
      const pageCanvasHeight = Math.floor((canvas.width * pdfPageHeight) / pdfPageWidth);
      let currentY = 0;
      let pageIndex = 0;

      while (currentY < canvas.height) {
        const sliceHeight = Math.min(pageCanvasHeight, canvas.height - currentY);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageCanvasHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvasHeight);
        ctx.drawImage(
          canvas,
          0, currentY, canvas.width, sliceHeight,
          0, 0, canvas.width, sliceHeight
        );

        if (pageIndex > 0) {
          doc.addPage();
        }
        const pageImgData = pageCanvas.toDataURL('image/png');
        doc.addImage(pageImgData, 'PNG', 0, 0, pdfPageWidth, pdfPageHeight);

        currentY += pageCanvasHeight;
        pageIndex++;
      }
    }
  }

  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  if (save) {
    doc.save(safeName);
    return undefined;
  }

  return doc.output('arraybuffer');
}

/**
 * Share sales order (invoice) as PDF via Web Share API or download fallback.
 *
 * @param {Object} order - Sales order object.
 * @param {Object} company - Company configuration settings.
 * @param {Object} [options] - Export options.
 */
export async function shareSalesOrderPdf(order, company, options = {}) {
  const filename = (options.filename ?? (order?.invoiceNumber || order?.orderNumber || 'invoice')).replace(
    /\s+/g,
    '-'
  );
  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  try {
    const arraybuffer = await exportSalesOrderToPdf(order, company, { ...options, save: false });
    if (!arraybuffer) return;
    const blob = new Blob([arraybuffer], { type: 'application/pdf' });

    let file = null;
    try {
      file = new File([blob], safeName, { type: 'application/pdf' });
    } catch (e) {
      // Ignore File constructor error
    }

    const canShareFiles =
      typeof navigator !== 'undefined' &&
      !!navigator.share &&
      !!navigator.canShare &&
      !!file &&
      navigator.canShare({ files: [file] });

    if (canShareFiles) {
      try {
        await navigator.share({
          title: `Invoice ${order?.invoiceNumber || order?.orderNumber || ''}`,
          text: company?.name ? `Invoice from ${company.name}` : 'Invoice',
          files: [file],
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('Navigator share error, falling back to download:', err);
      }
    }

    // Direct download fallback
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    console.error('Failed to share sales order PDF:', error);
    alert('Failed to generate or share PDF invoice. Please try again.');
  }
}
